import { ExpandMore, Notifications, Phone } from "@mui/icons-material";
import { Button, Card, CardActions, CardContent, CardHeader, CircularProgress, Collapse, Stack } from "@mui/material";
import { format } from "date-fns";
import { useState, type JSX } from "react";
import { useTranslation } from "react-i18next";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import ActionMenu from "../../components/action-menu/ActionMenu";
import { ActionMenuItem } from "../../components/action-menu/ActionMenuItem";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { useRecipientUsage } from "../../queries/recipients/UseRecipientUsage";
import { UserRecipientFieldArrayItemType } from "./UserRecipientFieldTypes";
import RecipientUsageList from "../recipient-usage-list/RecipientUsageList";



export type UserRecipientListItemProps = {
    recipient: UserRecipientFieldArrayItemType;
    onEdit?: (recipientKey: string) => void;
    onDisable?: (recipientKey: string, disable: boolean) => void;
    onDelete?: (recipientKey: string) => void;
    hideUsage?: boolean;
};

export default function UserRecipientListItem({
    recipient,
    onEdit,
    onDisable,
    onDelete,
    hideUsage
}: UserRecipientListItemProps): JSX.Element {

    const routeContext = useRouteContext();
    const { t } = useTranslation();
    const [usageExpanded, setUsageExpanded] = useState<boolean>(false);

    const {
        data,
        status
    } = useQueryNotifications(useRecipientUsage)({
        groupID: routeContext.groupID as number,
        recipientID: recipient.id,
        enabled: usageExpanded
    });

    const notificationLists = data?.filter(usage => usage.type === eLynxObjectTypeEnum.NotificationList) ?? [];
    const alarmSetpoints = data?.filter(usage => usage.type === eLynxObjectTypeEnum.SetPoint) ?? [];

    const disabledMessage = recipient.disabled
        ? recipient.disabledTimestamp
            ? t("userEditor.steps.alarmNotifications.disabledOn", { timestamp: format(new Date(recipient.disabledTimestamp), "P pp") })
            : "Disable Pending"
        : recipient.disabledTimestamp
            ? "Enabled Pending"
            : undefined;

    const actionItems = new Array<ActionMenuItem>();

    if (onEdit) {
        actionItems.push({
            label: t("actions.edit"),
            onClick: () => {
                onEdit(recipient.recipientKey);
            }
        });
    }

    if (onDisable) {
        actionItems.push({
            label: recipient.disabled
                ? t("actions.enable")
                : t("actions.disable"),
            onClick: () => {
                onDisable(recipient.recipientKey, !recipient.disabled);
            }
        });
    }

    if (onDelete) {
        actionItems.push({
            label: t("actions.delete"),
            onClick: () => {
                onDelete(recipient.recipientKey);
            }
        });
    }

    return (
        <Card variant="outlined">
            <CardHeader
                sx={{
                    padding: 1,
                    paddingBottom: 0
                }}
                title={
                    recipient.to
                }
                subheader={
                    disabledMessage
                }
                action={<ActionMenu actionItems={actionItems} />}
                slotProps={{
                    title: { variant: "body2" },
                    subheader: { variant: "body2" }
                }} />
            {!hideUsage && 
            <>
            <CardActions
                sx={{
                    padding: 1,
                    paddingTop: 0,
                    paddingBottom: 0
                }}
            >
                <Button
                    disabled={(recipient.usageCount ?? 0) === 0}
                    sx={{
                        color: "text.primary"
                    }}
                    variant="text"
                    onClick={
                        () => setUsageExpanded(prev => !prev)
                    }
                    style={{ marginLeft: "auto" }}
                    endIcon={
                        status === "loading"
                            ? <CircularProgress size={20} />
                            : <ExpandMore
                                sx={{
                                    transform: !usageExpanded ? "rotate(0deg)" : "rotate(180deg)",
                                    transition: theme => theme.transitions.create('transform', {
                                        duration: theme.transitions.duration.shortest,
                                    })
                                }}
                            />}
                >
                    {t("userEditor.steps.alarmNotifications.usage", { count: recipient.usageCount ?? 0 })}
                </Button>
            </CardActions>
            <Collapse in={usageExpanded} timeout="auto">
                <CardContent
                    sx={{
                        paddingTop: 0,
                        paddingBottom: theme => `${theme.spacing(0)} !important`
                    }}
                >
                    <Stack direction="column" spacing={1}>
                        <RecipientUsageList title={t("userEditor.steps.alarmNotifications.notificationLists")} icon={<Phone />} recipientUsages={notificationLists} />
                        <RecipientUsageList title={t("userEditor.steps.alarmNotifications.alarmSetpoints")} icon={<Notifications />} recipientUsages={alarmSetpoints} />
                    </Stack>
                </CardContent>
            </Collapse>
            </>            
            }
        </Card>
    );
}